import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileWarning,
  Layers,
  Plus,
  Rocket,
  Search,
  Sparkles,
  Upload,
  UserPlus,
} from 'lucide-react'
import { fetchOrganizations } from '@/lib/api/spaces.js'
import { fetchRecentPublishes } from '@/lib/api/publish.js'
import { useAuth } from '@/lib/auth/AuthContext.jsx'
import { useRecentFloors } from '@/lib/recents.js'

export default function DashboardPage() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const orgsQuery = useQuery({ queryKey: ['orgs'], queryFn: fetchOrganizations })
  const publishesQuery = useQuery({
    queryKey: ['publish', 'recent', 8],
    queryFn: () => fetchRecentPublishes(8),
  })

  const orgs = orgsQuery.data || []
  const recentFloors = useRecentFloors()
  const recentPublishes = publishesQuery.data || []

  const allFloors = useMemo(() => flattenFloors(orgs), [orgs])
  const attention = useMemo(() => deriveAttention(allFloors), [allFloors])
  const activity = useMemo(
    () => buildActivity(recentFloors, recentPublishes),
    [recentFloors, recentPublishes],
  )

  const greetingName =
    (user?.full_name && user.full_name.split(' ')[0]) ||
    (user?.email && user.email.split('@')[0]) ||
    'there'

  return (
    <section className="app-content">
      <header className="dash-greeting">
        <div>
          <h1 className="dash-greeting__title">Welcome back, {greetingName}</h1>
          <div className="dash-greeting__sub">
            Pick up where you left off, or clear what&rsquo;s blocking your next publish.
          </div>
        </div>
      </header>

      <div className="dash2-grid">
        <div className="dash2-main">
          <ResumeEditing
            recents={recentFloors}
            allFloors={allFloors}
            loading={orgsQuery.isLoading}
          />
          <NeedsAttention items={attention} loading={orgsQuery.isLoading} />
          <ActivityFeed items={activity} />
        </div>
        <aside className="dash2-side">
          <QuickActions onNavigate={navigate} />
          <FloorSearch floors={allFloors} />
          <RecentlyPublished items={recentPublishes} loading={publishesQuery.isLoading} />
        </aside>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Resume editing
// ---------------------------------------------------------------------------

function ResumeEditing({ recents, allFloors, loading }) {
  // Fall back to the first few floors in the workspace when the user hasn't
  // opened any floors yet (fresh login / new browser).
  const fallback = recents.length === 0
  const items = (fallback ? allFloors.slice(0, 4) : recents.slice(0, 4)).map((f) => ({
    id: f.id,
    name: f.name,
    building: f.building,
    organization: f.organization,
    counts: f.counts,
    publish_status: f.publish_status,
    visited_at: f.visited_at,
  }))

  return (
    <section className="card card--lg dash2-block">
      <header className="dash2-block__head">
        <div>
          <div className="card__title">Resume editing</div>
          <div className="card__subtitle">
            {fallback
              ? 'Jump into a floor to keep going.'
              : 'Picks up exactly where you were.'}
          </div>
        </div>
        <Link to="/spaces" className="btn btn--sm">
          All spaces <ChevronRight size={14} />
        </Link>
      </header>

      {loading ? (
        <div className="muted" style={{ padding: 12 }}>Loading floors…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Layers size={20} />}
          title="No floors yet"
          body="Create an organization, add a building, then add a floor to begin authoring."
          action={
            <Link to="/organizations" className="btn btn--primary btn--sm">
              <Plus size={14} /> New organization
            </Link>
          }
        />
      ) : (
        <div className="resume-grid">
          {items.map((f) => (
            <ResumeCard key={f.id} floor={f} />
          ))}
        </div>
      )}
    </section>
  )
}

function ResumeCard({ floor }) {
  const counts = floor.counts || {}
  return (
    <Link to={`/spaces/floors/${floor.id}`} className="resume-card">
      <div className="resume-card__thumb" aria-hidden>
        <ThumbDiagram seed={floor.id} />
      </div>
      <div className="resume-card__body">
        <div className="resume-card__title">{floor.name || 'Untitled floor'}</div>
        <div className="resume-card__sub">
          {[floor.building, floor.organization].filter(Boolean).join(' · ') || 'Workspace'}
        </div>
        <div className="resume-card__meta">
          <span>{counts.nodes || 0} nodes</span>
          <span>·</span>
          <span>{counts.anchors || 0} anchors</span>
          <span>·</span>
          <span>{counts.destinations || 0} destinations</span>
        </div>
      </div>
      <PublishBadge status={floor.publish_status} />
    </Link>
  )
}

// ---------------------------------------------------------------------------
// Needs attention
// ---------------------------------------------------------------------------

function NeedsAttention({ items, loading }) {
  return (
    <section className="card card--lg dash2-block">
      <header className="dash2-block__head">
        <div>
          <div className="card__title">Needs your attention</div>
          <div className="card__subtitle">
            Floors with blockers, missing pieces, or pending publishes.
          </div>
        </div>
        <span className="badge">
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </span>
      </header>

      {loading ? (
        <div className="muted" style={{ padding: 12 }}>Scanning your workspace…</div>
      ) : items.length === 0 ? (
        <div className="attention-empty">
          <CheckCircle2 size={18} />
          <span>Nothing to clean up. Everything is in good shape.</span>
        </div>
      ) : (
        <ul className="attention-list">
          {items.map((item) => (
            <li key={`${item.floor.id}:${item.code}`}>
              <Link to={`/spaces/floors/${item.floor.id}`} className="attention-row">
                <span className={`attention-icon attention-icon--${item.severity}`}>
                  {item.severity === 'blocker' ? (
                    <AlertTriangle size={14} />
                  ) : item.severity === 'warning' ? (
                    <FileWarning size={14} />
                  ) : (
                    <Sparkles size={14} />
                  )}
                </span>
                <div className="attention-body">
                  <div className="attention-title">{item.title}</div>
                  <div className="attention-sub">
                    {item.floor.name}
                    {item.floor.building ? ` · ${item.floor.building}` : ''}
                    {item.floor.organization ? ` · ${item.floor.organization}` : ''}
                  </div>
                </div>
                <ArrowRight size={14} className="attention-arrow" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function deriveAttention(floors) {
  const items = []
  for (const f of floors) {
    const counts = f.counts || {}
    const hasModel = (list) => (list || []).some(
      (a) => a.asset_type === 'fbx' || a.asset_type === 'glb' || a.asset_type === 'usdz',
    )
    const hasFbx = hasModel(f.assets) || hasModel(f.building_assets) || hasModel(f.site_assets)

    if (!hasFbx) {
      items.push({
        code: 'NO_FBX',
        severity: 'warning',
        title: 'No 3D model uploaded',
        floor: f,
      })
    }
    if (counts.nodes > 0 && counts.anchors === 0) {
      items.push({
        code: 'NO_ENTRY_ANCHOR',
        severity: 'blocker',
        title: 'Floor has no entry anchor',
        floor: f,
      })
    }
    if (counts.nodes > 0 && counts.destinations === 0) {
      items.push({
        code: 'NO_DESTINATIONS',
        severity: 'warning',
        title: 'No destinations defined',
        floor: f,
      })
    }
    if (counts.nodes === 0 && hasFbx) {
      items.push({
        code: 'EMPTY_GRAPH',
        severity: 'info',
        title: 'Model uploaded — no waypoints yet',
        floor: f,
      })
    }
    if (f.publish_status === 'pending_review') {
      items.push({
        code: 'AWAITING_REVIEW',
        severity: 'warning',
        title: 'Awaiting publish review',
        floor: f,
      })
    }
  }
  // Blockers first, then warnings, then info.
  const order = { blocker: 0, warning: 1, info: 2 }
  items.sort((a, b) => order[a.severity] - order[b.severity])
  return items.slice(0, 8)
}

// ---------------------------------------------------------------------------
// Activity feed
// ---------------------------------------------------------------------------

function buildActivity(recentFloors, recentPublishes) {
  const events = []
  for (const p of recentPublishes) {
    events.push({
      id: `pub:${p.id}`,
      kind: 'publish',
      at: p.published_at,
      title: `Published ${p.floor_name || 'floor'} v${p.version_number}`,
      sub: [p.building_name, p.organization_name].filter(Boolean).join(' · ') || p.change_summary,
      href: `/spaces/floors/${p.floor_id}`,
    })
  }
  for (const r of recentFloors) {
    events.push({
      id: `view:${r.id}:${r.visited_at}`,
      kind: 'view',
      at: r.visited_at,
      title: `Opened ${r.name}`,
      sub: [r.building, r.organization].filter(Boolean).join(' · '),
      href: `/spaces/floors/${r.id}`,
    })
  }
  events.sort((a, b) => new Date(b.at) - new Date(a.at))
  return events.slice(0, 8)
}

function ActivityFeed({ items }) {
  return (
    <section className="card card--lg dash2-block">
      <header className="dash2-block__head">
        <div>
          <div className="card__title">Activity</div>
          <div className="card__subtitle">
            Recent publishes and floors you&rsquo;ve opened.
          </div>
        </div>
      </header>

      {items.length === 0 ? (
        <div className="muted" style={{ padding: 12 }}>
          Nothing yet. Open a floor or publish a draft to see activity here.
        </div>
      ) : (
        <ul className="activity-list">
          {items.map((e) => (
            <li key={e.id}>
              <Link to={e.href} className="activity-row">
                <span className={`activity-dot activity-dot--${e.kind}`} aria-hidden>
                  {e.kind === 'publish' ? <Rocket size={12} /> : <Clock size={12} />}
                </span>
                <div className="activity-body">
                  <div className="activity-title">{e.title}</div>
                  {e.sub ? <div className="activity-sub">{e.sub}</div> : null}
                </div>
                <span className="activity-time">{relativeTime(e.at)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Side: Quick actions
// ---------------------------------------------------------------------------

function QuickActions() {
  return (
    <section className="card card--lg dash2-block">
      <header className="dash2-block__head">
        <div>
          <div className="card__title">Quick actions</div>
        </div>
      </header>
      <div className="quick-actions">
        <Link to="/organizations" className="quick-action">
          <span className="quick-action__icon"><Plus size={16} /></span>
          <div>
            <div className="quick-action__title">New organization</div>
            <div className="quick-action__sub">Create a tenant + first site</div>
          </div>
        </Link>
        <Link to="/spaces" className="quick-action">
          <span className="quick-action__icon"><Building2 size={16} /></span>
          <div>
            <div className="quick-action__title">Open spaces</div>
            <div className="quick-action__sub">Browse sites, buildings, floors</div>
          </div>
        </Link>
        <Link to="/spaces" className="quick-action quick-action--soft">
          <span className="quick-action__icon"><Upload size={16} /></span>
          <div>
            <div className="quick-action__title">Upload FBX</div>
            <div className="quick-action__sub">Pick a floor, then drop the model</div>
          </div>
        </Link>
        <Link to="/settings" className="quick-action quick-action--soft">
          <span className="quick-action__icon"><UserPlus size={16} /></span>
          <div>
            <div className="quick-action__title">Invite teammate</div>
            <div className="quick-action__sub">Manage admin access</div>
          </div>
        </Link>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Side: Floor search
// ---------------------------------------------------------------------------

function FloorSearch({ floors }) {
  const [q, setQ] = useState('')
  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return []
    return floors
      .filter((f) => {
        const hay = [f.name, f.building, f.site, f.organization]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return hay.includes(needle)
      })
      .slice(0, 6)
  }, [q, floors])

  return (
    <section className="card card--lg dash2-block">
      <header className="dash2-block__head">
        <div>
          <div className="card__title">Jump to a floor</div>
        </div>
      </header>
      <div className="floor-search">
        <Search size={14} className="floor-search__icon" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by floor, building, or org…"
          className="floor-search__input"
        />
      </div>
      {q && (
        matches.length === 0 ? (
          <div className="muted" style={{ padding: '8px 0', fontSize: 13 }}>
            No matches.
          </div>
        ) : (
          <ul className="floor-search__results">
            {matches.map((f) => (
              <li key={f.id}>
                <Link to={`/spaces/floors/${f.id}`} className="floor-search__result">
                  <div>
                    <div className="floor-search__name">{f.name}</div>
                    <div className="floor-search__meta">
                      {[f.building, f.organization].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <ChevronRight size={14} />
                </Link>
              </li>
            ))}
          </ul>
        )
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Side: Recently published
// ---------------------------------------------------------------------------

function RecentlyPublished({ items, loading }) {
  return (
    <section className="card card--lg dash2-block">
      <header className="dash2-block__head">
        <div>
          <div className="card__title">Recently published</div>
          <div className="card__subtitle">Live versions on the runtime API.</div>
        </div>
      </header>
      {loading ? (
        <div className="muted" style={{ padding: 8 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="muted" style={{ padding: 8, fontSize: 13 }}>
          Nothing published yet.
        </div>
      ) : (
        <ul className="published-list">
          {items.slice(0, 5).map((p) => (
            <li key={p.id}>
              <Link to={`/spaces/floors/${p.floor_id}`} className="published-row">
                <div>
                  <div className="published-title">
                    {p.floor_name} <span className="muted">v{p.version_number}</span>
                  </div>
                  <div className="published-sub">
                    {[p.building_name, p.organization_name].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <span className="published-time">{relativeTime(p.published_at)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Bits & helpers
// ---------------------------------------------------------------------------

function PublishBadge({ status }) {
  if (status === 'published') return <span className="badge badge--ok">Live</span>
  if (status === 'pending_review') return <span className="badge badge--warn">Review</span>
  return <span className="badge">Draft</span>
}

function EmptyState({ icon, title, body, action }) {
  return (
    <div className="dash2-empty">
      <div className="dash2-empty__icon">{icon}</div>
      <div className="dash2-empty__title">{title}</div>
      <div className="dash2-empty__body">{body}</div>
      {action ? <div style={{ marginTop: 10 }}>{action}</div> : null}
    </div>
  )
}

function ThumbDiagram({ seed = '' }) {
  // Cheap deterministic "graph-like" sketch so each thumbnail looks distinct.
  const h = hashString(seed)
  const offsetA = ((h % 30) - 15)
  const offsetB = (((h >> 4) % 30) - 15)
  const offsetC = (((h >> 8) % 30) - 15)
  return (
    <svg viewBox="0 0 200 110" width="100%" height="100%" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`bg-${h}`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#eef4ff" />
          <stop offset="100%" stopColor="#f5f7fb" />
        </linearGradient>
        <linearGradient id={`route-${h}`} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#2f6bff" />
          <stop offset="100%" stopColor="#7fa4ff" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="200" height="110" fill={`url(#bg-${h})`} />
      <path
        d={`M14 ${80 + offsetA} L60 ${70 + offsetB} L110 ${50 + offsetC} L160 ${72 + offsetA} L188 ${60 + offsetB}`}
        stroke={`url(#route-${h})`}
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {[
        [14, 80 + offsetA],
        [60, 70 + offsetB],
        [110, 50 + offsetC],
        [160, 72 + offsetA],
        [188, 60 + offsetB],
      ].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i === 0 || i === 4 ? 5 : 3.2} fill="#fff" stroke="#2f6bff" strokeWidth="1.6" />
      ))}
    </svg>
  )
}

function hashString(s) {
  let h = 0
  for (let i = 0; i < (s || '').length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0
  }
  return h
}

function flattenFloors(orgs) {
  const out = []
  for (const org of orgs) {
    for (const site of org.sites || []) {
      for (const building of site.buildings || []) {
        for (const floor of building.floors || []) {
          out.push({
            id: floor.id,
            name: floor.name,
            building: building.name,
            site: site.name,
            organization: org.name,
            counts: floor.counts,
            assets: floor.assets,
            building_assets: building.assets,
            site_assets: site.assets,
            publish_status: floor.publish_status,
          })
        }
      }
    }
  }
  return out
}

function relativeTime(iso) {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms)) return ''
  if (ms < 60_000) return 'just now'
  const m = Math.round(ms / 60_000)
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.round(h / 24)
  if (d < 30) return `${d}d ago`
  const mo = Math.round(d / 30)
  return `${mo}mo ago`
}
